// FILE: frontend/src/pages/ClientsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGetAll, apiPost, apiDelete, getClientGroups, resolveClientGroup } from '../api';
import VirtualTableRows from '../components/VirtualTableRows';
import MergeClientsModal from '../components/MergeClientsModal';
import {
  AddMasterFileCheckbox, addMasterFile, useMasterFileConfigured, wantsMasterFile,
} from '../components/masterFile';

const SEARCH_DEBOUNCE_MS = 250;

const isManager = (user) =>
  ['MANAGER', 'ADMIN'].includes(String(user?.role).toUpperCase());

export default function ClientsPage({ user }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  // The value actually sent to the API. Trails `search` by SEARCH_DEBOUNCE_MS so
  // we fire one request per pause in typing rather than one per keystroke --
  // each request walks every page of /api/clients/.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');  // active|inactive|all
  const [groupFilter, setGroupFilter] = useState('');
  // Client keeps its own books in QuickBooks Online: '' (any) | 'yes' | 'no'.
  const [qboFilter, setQboFilter] = useState('');
  const [groups, setGroups] = useState([]);
  const [mode, setMode] = useState('all');        // 'all' | 'groups'
  const [sortBy, setSortBy] = useState('name');  // 'name' | '-name' | 'transactions_count' | '-transactions_count'
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    getClientGroups().then(setGroups).catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    // Responses can land out of order: an earlier, broader request (say "S")
    // may resolve after a later, narrower one ("Smith") and overwrite it,
    // snapping the list back to everyone. Ignore any response that is no longer
    // the current one.
    let cancelled = false;

    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (activeFilter === 'active') params.set('is_active', 'true');
    if (activeFilter === 'inactive') params.set('is_active', 'false');
    if (groupFilter) params.set('group', groupFilter);
    if (qboFilter === 'yes') params.set('uses_qbo_online', 'true');
    if (qboFilter === 'no') params.set('uses_qbo_online', 'false');
    params.set('ordering', sortBy);
    const qs = params.toString();
    apiGetAll(`/api/clients/${qs ? '?' + qs : ''}`)
      .then(data => {
        if (cancelled) return;
        setClients(Array.isArray(data) ? data : (data?.results || []));
      })
      .catch(() => {
        if (!cancelled) setClients([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // reloadCounter intentionally a dep so create-modal save triggers refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeFilter, groupFilter, qboFilter, sortBy, reloadCounter]);

  const toggleSort = (col) => setSortBy(sortBy === col ? `-${col}` : col);
  // Yes/blank column: the first click should bring the Yes rows to the top,
  // so it starts descending (true before false), then flips.
  const toggleSortDescFirst = (col) => setSortBy(sortBy === `-${col}` ? col : `-${col}`);

  // Year-end label.
  // Personal clients always have year-end Dec 31.
  // Corporate clients use fiscal_month_end (1-12) -> last day of that month.
  const fmtYearEnd = (client) => {
    if (!client.is_corporate) return 'December 31';
    const m = Number(client.fiscal_month_end);
    if (!m || m < 1 || m > 12) return '';
    const months = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December'];
    const lastDay = new Date(2001, m, 0).getDate(); // non-leap year for Feb=28
    return `${months[m - 1]} ${lastDay}`;
  };

  const sortIndicator = (col) => {
    if (sortBy === col) return ' ↑';
    if (sortBy === `-${col}`) return ' ↓';
    return ' ⇅';
  };

  const totalCount = clients.length;

  // The ticked rows, as objects and in the order they are listed. Derived from
  // the rows on screen rather than from `selected` alone: a tick survives a
  // filter change, and merging a client you can no longer see would be a
  // nasty surprise.
  const selectedClients = clients.filter((c) => selected.has(c.id));

  const handleMerge = async (targetId, sourceIds) => {
    const result = await apiPost('/api/clients/merge/', {
      target_id: targetId,
      source_ids: sourceIds,
    });
    setSelected(new Set());
    setReloadCounter((n) => n + 1);
    return result;
  };

  // TODO: Assignee filter -- needs /api/employees/ fetch + multi-select; deferred.
  // TODO: Group management modal -- v1 stub.

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Clients</h1>
          <div style={{ color: '#6b7280', fontSize: 14 }}>{totalCount} Clients</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selected.size > 0 && (
            <button
              onClick={async () => {
                const ids = Array.from(selected);
                const label = ids.length === 1 ? '1 client' : `${ids.length} clients`;
                if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
                try {
                  await Promise.all(ids.map((id) => apiDelete(`/api/clients/${id}/`)));
                  setSelected(new Set());
                  setReloadCounter((n) => n + 1);
                } catch (e) {
                  alert(`Delete failed: ${e.message || e}`);
                }
              }}
              style={{ ...btnStyle, background: '#dc2626', color: 'white', border: 'none', fontWeight: 600 }}
            >
              Delete ({selected.size})
            </button>
          )}
          {isManager(user) && selectedClients.length > 1 && (
            <button
              onClick={() => setShowMergeModal(true)}
              style={{ ...btnStyle, fontWeight: 600 }}
            >
              Merge ({selectedClients.length})
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            style={{ ...btnStyle, background: '#059669', color: 'white', border: 'none', fontWeight: 600 }}
          >
            + New Client
          </button>
          <button onClick={() => alert('Group management -- TODO')} style={btnStyle}>+ Group</button>
          <button title="About" style={iconBtnStyle}>{'ⓘ'}</button>
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{
        background: '#f3f4f6', borderRadius: 8, padding: 4, display: 'inline-flex',
        marginBottom: 16, width: 'fit-content',
      }}>
        {[['all', 'All'], ['groups', 'By group']].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            style={{
              padding: '6px 18px', border: 'none', borderRadius: 6, cursor: 'pointer',
              fontWeight: 600,
              background: mode === key ? '#3b82f6' : 'transparent',
              color: mode === key ? 'white' : '#6b7280',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'groups' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          {groups.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>No groups yet.</div>
          ) : groups.map((g) => (
            <div
              key={g.id}
              onClick={() => navigate(`/clients/groups/${g.id}`)}
              style={{
                padding: '14px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <span style={{ fontWeight: 600, color: '#111827' }}>{g.name}</span>
              <span style={{ color: '#6b7280', fontSize: 13 }}>
                {g.client_count} {g.client_count === 1 ? 'client' : 'clients'}
              </span>
            </div>
          ))}
        </div>
      )}

      {mode === 'all' && (
        <>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by any field (name, email, phone, etc)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 280, padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db' }}
            />
            <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)} style={selectStyle}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
            <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} style={selectStyle}>
              <option value="">All groups</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <select value={qboFilter} onChange={(e) => setQboFilter(e.target.value)}
                    aria-label="QuickBooks Online filter"
                    title="Clients who keep their own books in QuickBooks Online"
                    style={selectStyle}>
              <option value="">Any bookkeeping</option>
              <option value="yes">QBO Online only</option>
              <option value="no">Not QBO Online</option>
            </select>
            <button title="Export current view to CSV"
                    onClick={() => exportClientsCsv(clients)}
                    style={iconBtnStyle}
                    disabled={!clients.length}>
              {'⬇'}
            </button>
          </div>

          {/* Table */}
          <div ref={scrollRef} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '40px' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '9%' }} />
              </colgroup>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>
                    <input
                      type="checkbox"
                      checked={clients.length > 0 && clients.every((c) => selected.has(c.id))}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) {
                          clients.forEach((c) => next.add(c.id));
                        } else {
                          clients.forEach((c) => next.delete(c.id));
                        }
                        setSelected(next);
                      }}
                    />
                  </th>
                  <th style={thStyle} onClick={() => toggleSort('name')}>CLIENT{sortIndicator('name')}</th>
                  <th style={thStyle} onClick={() => toggleSort('group__name')}>GROUP{sortIndicator('group__name')}</th>
                  <th style={thStyle}>CONTACT</th>
                  <th style={thStyle}>EMAIL</th>
                  <th style={thStyle}>PHONE</th>
                  <th style={thStyle} onClick={() => toggleSort('transactions_count')}>TRANSACTIONS{sortIndicator('transactions_count')}</th>
                  <th style={thStyle} onClick={() => toggleSortDescFirst('uses_qbo_online')}
                      title="Client keeps its own books in QuickBooks Online">QBO ONLINE{sortIndicator('uses_qbo_online')}</th>
                  <th style={thStyle}>ENTITY TYPE</th>
                  <th style={thStyle}>YEAR END</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
                ) : clients.length === 0 ? (
                  <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>No clients match these filters.</td></tr>
                ) : (
                  <VirtualTableRows count={clients.length} scrollRef={scrollRef} colSpan={10} estimateSize={45}>
                    {(index, measureRef) => {
                      const c = clients[index];
                      return (
                        <tr key={c.id} ref={measureRef} data-index={index}
                            style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                            onClick={() => navigate(`/clients/${c.id}`)}>
                          <td style={tdStyle} onClick={e => e.stopPropagation()}>
                            <input type="checkbox"
                                   checked={selected.has(c.id)}
                                   onChange={() => {
                                     const next = new Set(selected);
                                     if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                                     setSelected(next);
                                   }} />
                          </td>
                          <td style={{ ...tdStyle, color: '#059669', fontWeight: 600 }}>{c.name}</td>
                          <td style={tdStyle}>
                            {c.group ? (
                              <span
                                role="link"
                                title={`Open group ${c.group_name}`}
                                style={{ color: '#2563eb', cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); navigate(`/clients/groups/${c.group}`); }}
                              >
                                {c.group_name}
                              </span>
                            ) : ''}
                          </td>
                          <td style={tdStyle}>{c.contact || ''}</td>
                          <td style={tdStyle}>{c.email || ''}</td>
                          <td style={tdStyle}>{c.phone || ''}</td>
                          <td style={tdStyle}>{c.transactions_count ?? 0}</td>
                          <td style={tdStyle}>{c.uses_qbo_online ? 'Yes' : ''}</td>
                          <td style={tdStyle}>{c.is_corporate ? 'Corporate' : 'Personal'}</td>
                          <td style={tdStyle}>{fmtYearEnd(c)}</td>
                        </tr>
                      );
                    }}
                  </VirtualTableRows>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showMergeModal && (
        <MergeClientsModal
          isOpen={showMergeModal}
          clients={selectedClients}
          onClose={() => setShowMergeModal(false)}
          onMerge={handleMerge}
        />
      )}

      {showCreateModal && (
        <CreateClientModal
          groups={groups}
          onGroupsChanged={() => getClientGroups().then(setGroups).catch(() => {})}
          // returns the promise so the modal can wait for the new option to exist
          onClose={() => setShowCreateModal(false)}
          onCreated={(client, masterFile) => {
            setShowCreateModal(false);
            setReloadCounter((n) => n + 1);
            // The master-file outcome rides along to the client page, which
            // shows it as a banner (ClientDetailPage reads location.state).
            if (client?.id) {
              navigate(`/clients/${client.id}`, masterFile ? { state: { masterFile } } : undefined);
            }
          }}
        />
      )}
    </div>
  );
}

function CreateClientModal({ onClose, onCreated, groups, onGroupsChanged }) {
  const [name, setName] = useState('');
  const [isCorporate, setIsCorporate] = useState(false);
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // The group is an id, not a name. This used to be a free-text box whose
  // contents went to resolveClientGroup on submit; the backend's name matching
  // is sound (casefolded, so "smith holdings" joins "Smith Holdings"), so the
  // problem was never duplicates -- it was that you could not see what groups
  // existed, and a typo quietly made a new one. Picking from a list needs no
  // resolution at all: we already have the id.
  const [groupId, setGroupId] = useState(null);
  const groupSelectRef = useRef(null);
  const [fiscalMonthEnd, setFiscalMonthEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('Creating…');
  const [err, setErr] = useState('');
  const masterFileConfigured = useMasterFileConfigured(isCorporate);
  const [addMaster, setAddMaster] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setBusy(true);
    setBusyLabel('Creating…');
    setErr('');
    let created;
    try {
      created = await apiPost('/api/clients/', {
        name: name.trim(),
        is_corporate: isCorporate,
        contact: contact.trim() || '',
        email: email.trim() || '',
        phone: phone.trim() || '',
        group: groupId,
        fiscal_month_end: fiscalMonthEnd ? parseInt(fiscalMonthEnd, 10) : null,
        is_active: true,
      });
    } catch (e) {
      setErr(e.message || 'Failed to create');
      setBusy(false);
      return;
    }
    // The client exists from here on. The master file is a second step whose
    // failure is reported on the client's page, never as a failed create.
    let masterFile = null;
    if (wantsMasterFile(masterFileConfigured, addMaster) && created?.id) {
      setBusyLabel('Creating master file…');
      masterFile = await addMasterFile(created.id);
    }
    setBusy(false);
    onCreated(created, masterFile);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white', borderRadius: 8, width: 460, padding: 24,
      }}>
        <h3 style={{ marginTop: 0 }}>New Client</h3>
        <form onSubmit={submit}>
          <Field label="Name *" value={name} onChange={setName} autoFocus />
          <div style={{ marginBottom: 12 }}>
            <Label>Entity Type</Label>
            <select value={isCorporate ? 'corporate' : 'personal'}
                    onChange={(e) => setIsCorporate(e.target.value === 'corporate')}
                    style={inputStyle}>
              <option value="personal">Personal (T1)</option>
              <option value="corporate">Corporate (T2)</option>
            </select>
          </div>
          <Field label="Contact" value={contact} onChange={setContact} />
          <Field label="Email" value={email} onChange={setEmail} type="email" />
          <Field label="Phone" value={phone} onChange={setPhone} />
          <div style={{ marginBottom: 12 }}>
            <Label>Group</Label>
            <select
              aria-label="Group"
              ref={groupSelectRef}
              value={groupId ?? ''}
              onChange={async (e) => {
                const { value } = e.target;
                if (value !== '__new__') {
                  setGroupId(value ? Number(value) : null);
                  return;
                }
                // The way out of "my group isn't listed". Cancelling must leave
                // the previous pick alone, and that takes an imperative reset:
                // the DOM select has already moved to __new__, but `groupId` has
                // not changed, so there is no re-render coming to put it back.
                // eslint-disable-next-line no-alert
                const newName = window.prompt('New group name');
                if (!newName?.trim()) {
                  if (groupSelectRef.current) groupSelectRef.current.value = groupId ?? '';
                  return;
                }
                const id = await resolveClientGroup(newName);
                // Refresh the options BEFORE pointing the value at the new id.
                // A <select> cannot hold a value with no matching <option>, so
                // setting the id first makes the control render blank until the
                // refetch lands -- and blank is exactly what "no group" looks like.
                await onGroupsChanged?.();
                setGroupId(id);
              }}
              style={inputStyle}
            >
              <option value="">— No group —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
              <option value="__new__">＋ New group…</option>
            </select>
          </div>
          {isCorporate && (
            <div style={{ marginBottom: 12 }}>
              <Label>Fiscal Year-End Month</Label>
              <select value={fiscalMonthEnd}
                      onChange={(e) => setFiscalMonthEnd(e.target.value)}
                      style={inputStyle}>
                <option value="">— Select —</option>
                {['January','February','March','April','May','June',
                  'July','August','September','October','November','December'].map((name, i) => (
                  <option key={i + 1} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <AddMasterFileCheckbox
              configured={masterFileConfigured}
              isCorporate={isCorporate}
              checked={addMaster}
              onChange={setAddMaster}
            />
          </div>
          {err && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={busy}
                    style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    style={{ padding: '8px 14px', border: 'none', borderRadius: 6, background: '#059669', color: 'white', cursor: 'pointer', fontWeight: 600 }}>
              {busy ? busyLabel : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>{children}</div>;
}

function Field({ label, value, onChange, type = 'text', autoFocus }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Label>{label}</Label>
      {/* The visible label is a <div>, so it names nothing. Without this the
          input has no accessible name at all -- for a screen reader or a test. */}
      <input type={type} value={value} autoFocus={autoFocus} aria-label={label}
             onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6,
  fontSize: 14, background: 'white', boxSizing: 'border-box',
};

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function clientsCsv(rows) {
  const cols = [
    { header: 'Name', key: 'name' },
    { header: 'Contact', key: 'contact' },
    { header: 'Email', key: 'email' },
    { header: 'Phone', key: 'phone' },
    { header: 'Transactions', key: 'transactions_count' },
    { header: 'Entity Type', get: (r) => (r.is_corporate ? 'Corporate' : 'Personal') },
    { header: 'Year End Month', key: 'fiscal_month_end' },
    { header: 'Group', key: 'group_name' },
    { header: 'Active', get: (r) => (r.is_active === false ? 'No' : 'Yes') },
    { header: 'QBO Online', get: (r) => (r.uses_qbo_online ? 'Yes' : '') },
  ];
  const header = cols.map((c) => csvEscape(c.header)).join(',');
  const body = rows.map((r) =>
    cols.map((c) => csvEscape(c.get ? c.get(r) : r[c.key] ?? '')).join(',')
  );
  return [header, ...body].join('\n');
}

function exportClientsCsv(rows) {
  if (!rows || rows.length === 0) return;
  const csv = clientsCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `clients-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const btnStyle = {
  padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6,
  background: 'white', cursor: 'pointer', fontSize: 14,
};
const iconBtnStyle = {
  ...btnStyle, padding: '6px 10px',
};
const selectStyle = {
  padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db',
  background: 'white', fontSize: 14,
};
const thStyle = {
  padding: '12px 14px', textAlign: 'left', fontSize: 11,
  fontWeight: 700, color: '#374151', letterSpacing: '0.05em', cursor: 'pointer',
};
const tdStyle = {
  padding: '12px 14px', fontSize: 14, color: '#111827',
};
